// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import moment from 'moment';
import { getCustomerOrderStatus } from 'Utils/orderStatus';
import getTotalChargedCredits from 'Utils/totalChargedCreditsUtil';
import { licenseFeeStatusLabel } from 'Utils/licenseFee';

// The Order History CSV, one row per placed order. Pure, so the column set and the redaction rules (sales-no-credits,
// sales-no-dollars) are tested without the page. The page passes the team list and, outside Treasure Chest, the
// project link builder.

export interface IOrderHistoryCsvOptions {
  salesNoCredits?: boolean;
  salesNoDollars?: boolean;
  teams?: { _id: string; name: string }[];
  projectLink?: (ownerProject: string) => string;
}

export const orderHistoryCsvHeaders = ({ salesNoCredits = false, salesNoDollars = false, admin = false }: { salesNoCredits?: boolean; salesNoDollars?: boolean; admin?: boolean }) => [
  { label: 'Order Name', key: 'orderName' },
  { label: 'Reference ID', key: 'referenceId' },
  { label: 'Team', key: 'team' },
  { label: 'Date', key: 'date' },
  { label: 'Status', key: 'status' },
  { label: 'Orders Acres', key: 'orderAcres' },
  { label: 'Project Mileage', key: 'projectMileage' },
  ...salesNoCredits ? [] : [
    { label: 'Charged Credits', key: 'chargedAcres' },
    { label: 'Imagery Credits', key: 'imageryCreditCost' },
    { label: 'Point Cloud Rasterization', key: 'rasterizationCreditCost' },
  ],
  { label: 'Project Size', key: 'projectSize' },
  { label: 'Bundle', key: 'bundle' },
  { label: 'Density', key: 'density' },
  { label: 'Delivery Speed', key: 'deliverySpeed' },
  { label: 'Add ons', key: 'addOns' },
  ...salesNoCredits ? [] : [
    { label: 'Adjusted Credits', key: 'adjustedCredits' },
    { label: 'Total Adjustment', key: 'totalAdjustment' },
  ],
  ...salesNoDollars ? [] : [
    { label: 'Total Cost for Project', key: 'totalCost' },
    { label: 'Additional Charges', key: 'additionalCost' },
    // The license fee the order was placed under (AUT-6569); blank for an order that predates the record
    { label: 'License Fee', key: 'licenseFee' },
    { label: 'License Fee Status', key: 'licenseFeeStatus' },
  ],
  ...admin ? [] : [{ label: 'Project Link', key: 'projectLink' }],
];

export const buildOrderHistoryCsvRow = (order: IOrder, { salesNoCredits = false, salesNoDollars = false, teams = [], projectLink }: IOrderHistoryCsvOptions = {}) => {
  const team = teams.find((t) => t._id === order.team);
  let addOnsList;
  let adjustmentList;
  if (order.addOns?.length > 0) {
    // a catalog add-on may carry no cost or acres (data add-ons priced elsewhere): the label stands alone then
    addOnsList = order.addOns.map((addOn) => {
      const amounts = [
        ...!salesNoCredits && typeof addOn.acres === 'number' ? [`${addOn.acres.toFixed(2)} ac`] : [],
        ...!salesNoDollars && typeof addOn.cost === 'number' ? [`$${addOn.cost.toFixed(2)}`] : [],
      ];
      const label = addOn.name || addOn.addOnType;
      return amounts.length ? `[${amounts.join(', ')}] - ${label}` : `- ${label}`;
    }).join(' \n');
  }
  if (order.creditAdjustments?.length > 0) {
    adjustmentList = order.creditAdjustments.map((adjsmnt) => `[${`${adjsmnt.adjustment >= 0 ? '+' : ''}${adjsmnt.adjustment.toFixed(2)} cr`}] - ${adjsmnt.reason}`).join(' \n');
  }

  const totalCreditsCharged = getTotalChargedCredits(order);

  const data: Record<string, unknown> = {
    orderName: order.name,
    referenceId: order.referenceId || 'N/A',
    team: team?.name || 'N/A',
    date: order.orderDate ? moment.utc(order.orderDate).local().format('MMMM D, YYYY') : 'N/A',
    status: getCustomerOrderStatus(order) || 'N/A',
    orderAcres: order.acreage,
    projectMileage: order.kmlLength?.toFixed(2),
    ...!salesNoCredits && {
      // This includes addOn acres
      chargedAcres: totalCreditsCharged,
      imageryCreditCost: order.imageryCreditCost ? `${order.imageryProvider} - ${order.imageryCreditCost} cr` : 'N/A',
      rasterizationCreditCost: order.rasterizationCreditCost ? `${order.rasterizationCreditCost} cr` : 'N/A',
      adjustedCredits: adjustmentList,
      totalAdjustment: order.adjustedAcres,
    },
    projectSize: order.acreage,
    bundle: order.bundleName,
    density: order.density,
    deliverySpeed: order.deliverySpeed,
    addOns: addOnsList || 'N/A',
    ...!salesNoDollars && {
      totalCost: order.costToCustomer ? `$${order.costToCustomer.toFixed(2)}` : 'N/A',
      additionalCost: order.paidForThisProject ? `$${order.paidForThisProject.toFixed(2)}` : 'N/A',
      // the amount added for 'added', the covered amount for 'prepaid', $0.00 for 'none'; blank before the record
      licenseFee: order.licenseFeeMode ? `$${(order.licenseFeeDollar || 0).toFixed(2)}` : '',
      licenseFeeStatus: licenseFeeStatusLabel(order.licenseFeeMode),
    },
  };
  if (projectLink) data.projectLink = projectLink(order.ownerProject);
  return data;
};
